import { Button } from '@/components/ui/button';
import { Phone, Mail, MessageCircle } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';

interface CTASectionProps {
  onRegisterClick: () => void;
  onConsultClick: () => void;
  onWhatsAppClick: () => void;
}

export const CTASection = ({ onRegisterClick, onConsultClick, onWhatsAppClick }: CTASectionProps) => {
  return (
    <section className="py-24 lg:py-36 water-ripple relative overflow-hidden">
      {/* Ghosted lifestyle background — friends laughing (Unsplash, royalty-free) */}
      <img
        src="/img/family-on-beach.jpg"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover opacity-[0.95]"
        loading="lazy"
      />
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-brand-teal/10 via-background to-brand-navy/10" />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-4xl lg:text-6xl font-semibold text-foreground mb-8">
            Be the first to know.
          </h2>
          <p className="text-xl text-muted-foreground mb-16 max-w-2xl mx-auto leading-relaxed">
            Register for updates or book a 15-minute consult with our team to learn more about
            residence options, pricing, and purchase process.
          </p>

          {/* Primary CTAs */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
            <Button
              size="lg"
              variant="luxury"
              className="text-lg px-10 py-7 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105"
              onClick={() => {
                trackEvent('cta_click', { location: 'cta_section', action: 'register' });
                onRegisterClick();
              }}
            >
              <Mail className="mr-2 h-5 w-5" />
              Register Interest
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="text-lg px-10 py-7 border-2 hover:bg-brand-teal hover:text-white hover:border-brand-teal transition-all duration-300 transform hover:-translate-y-1 hover:scale-105"
              onClick={() => {
                trackEvent('cta_click', { location: 'cta_section', action: 'book_consult' });
                onConsultClick();
              }}
            >
              <Phone className="mr-2 h-5 w-5" />
              Book Consult
            </Button>
          </div>

          {/* Alternative Contact */}
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <span className="text-sm">Or reach us instantly:</span>
            <Button
              variant="link"
              className="text-brand-teal h-auto p-0 hover:text-brand-teal/80 transition-colors duration-300 hover:scale-105 transform"
              onClick={() => {
                trackEvent('whatsapp_click', { location: 'cta_section' });
                onWhatsAppClick();
              }}
            >
              <MessageCircle className="w-4 h-4 mr-1" />
              WhatsApp
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
